import { useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Case, Default, Switch, When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { LinkButton } from '@/atoms/Button'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import {
  AsH1,
  AsH2,
  AsSpan,
  CaptionMD,
  HeadingXS,
  LabelXS,
  ParagraphMD,
  ParagraphSM,
  TitleLG,
  TitleXS,
} from '@/atoms/Text'
import { buildLinkTabModel, ComingSoonNode } from '@/services/ComingSoonService'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '../GuildJoinView/GuildEmailInput'

interface ComingSoonViewProps {
  comingSoonNodes: ComingSoonNode[]
}

export const ComingSoonView: React.FC<ComingSoonViewProps> = ({ comingSoonNodes }) => {
  const { t } = useTranslate('coming-soon')
  const { isLoggedIn } = useUser()
  const { isGuildMember } = useGuildMember()
  const { loginUrl, trackLoginStarted } = useLoginUrl()

  return (
    <main className="bg-core-gray-950 pb-32 pt-16 text-white">
      <PaddedContainer>
        <div className="flex flex-col gap-2 pt-8 lg:pt-12">
          <TitleLG className="lg:photon-heading-lg" as={AsH1} weight="semibold">
            {t('comingSoon', 'Coming Soon')}
          </TitleLG>
          <ParagraphSM color="core-gray-400">
            <Switch>
              <Case condition={isGuildMember}>
                {t('guildMembersLikeYou', 'Guild members like you made these releases happen.')}
              </Case>
              <Default>
                {t(
                  'guildMembersMakePossible',
                  'Guild members have made these releases happen. Join the guild today to help bring to life more shows that amplify light!',
                )}
              </Default>
            </Switch>
          </ParagraphSM>
          <When condition={!isGuildMember}>
            <div className="mt-4">
              <GuildEmailInput variant="left-white" hasUser={isLoggedIn} isCentered={false} />
            </div>
          </When>
          <When condition={!isLoggedIn}>
            <LabelXS>
              <Translate t={t} i18nKey="logInIfGuildMember">
                Already a member of the Angel Guild?&nbsp;
                <InternalLink
                  className="text-oxide-bright underline"
                  href={loginUrl}
                  onClick={() => trackLoginStarted('ComingSoonView')}
                >
                  Log In
                </InternalLink>
              </Translate>
            </LabelXS>
          </When>
          <div className="pt-8 lg:pt-12">
            <ul className="flex flex-col gap-12 border-l-2 border-gray-800 lg:gap-20">
              {comingSoonNodes.map((n) => {
                if (!n) return null
                return <ComingSoonItem key={n.id} node={n} />
              })}
            </ul>
          </div>
        </div>
      </PaddedContainer>
    </main>
  )
}

interface ComingSoonItemProps {
  node: ComingSoonNode
}

const ComingSoonItem: React.FC<ComingSoonItemProps> = ({ node }) => {
  const { t } = useTranslate('coming-soon')
  const track = useSafeTrack()
  const [isLoadingImage, setLoadingImage] = useState(true)
  const onLoadComplete = useCallback(() => setLoadingImage(false), [])
  const { isGuildMember } = useGuildMember()
  const linkModel = useMemo(() => buildLinkTabModel(node), [node])

  const handleClick = useCallback(() => {
    if (!linkModel?.track) return

    track(linkModel.track?.eventName, linkModel.track?.payload)
  }, [track, linkModel])

  if (!node) return null
  if (!linkModel || !linkModel.imageUrl) return null

  const isTheatrical = node?.focus?.type === 'theatrical'
  const isGuildEarlyAccess = node?.focus?.type === 'guild-access'
  const isPublicAccess = node?.focus?.type === 'public-access'

  const title = node.title ?? node?.watchable?.name ?? node?.project?.name

  const dateDisplayLong = node.header

  return (
    <LazyHydrate whenVisible>
      <li>
        <div className="relative">
          <div className="absolute left-[-5px] top-[6px] h-2 w-2 rounded-full border-2 border-white bg-black"></div>

          <div className="ml-5 lg:ml-7">
            <div className="mb-4">
              <TitleXS as={AsSpan} className="lg:photon-title-xs uppercase" weight="normal">
                {dateDisplayLong ?? t('toBeAnnounced', 'To Be Announced')}
              </TitleXS>
            </div>
            <div className="flex flex-col gap-4 lg:flex-row">
              <div>
                <InternalLink href={linkModel.linkUrl} onClick={handleClick}>
                  <div
                    className={classNames(
                      'relative cursor-pointer overflow-hidden rounded-xl bg-cover duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.05]',
                    )}
                  >
                    <div className={classNames(['bg-black', isLoadingImage ? 'animate-pulse' : ''])}>
                      <div className="aspect-h-9 aspect-w-16 relative w-full lg:w-[560px] xl:w-[790px]">
                        <Image
                          className="absolute left-0 top-0 h-full w-full rounded-xl object-cover"
                          src={getCloudinaryImageUrl({ path: linkModel.imageUrl, width: 790, height: 444 })}
                          alt={linkModel.alt || ''}
                          onLoadingComplete={onLoadComplete}
                          width={790}
                          height={444}
                        />
                      </div>
                    </div>
                  </div>
                </InternalLink>
              </div>
              <div className="flex w-full flex-col gap-4 rounded-xl bg-core-gray-900 p-6">
                <HeadingXS className="lg:photon-heading-sm" as={AsH2}>
                  {title}
                </HeadingXS>
                <ParagraphMD>{node.watchable.description}</ParagraphMD>
                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <Switch>
                      <Case condition={!dateDisplayLong}>
                        <PlaySquareIcon color="info-200" size={24} />
                        <CaptionMD color="info-200">{t('toBeAnnounced', 'To Be Announced')}</CaptionMD>
                      </Case>
                      <Case condition={isTheatrical}>
                        <TicketIcon color="info-200" size={24} />
                        <CaptionMD color="info-200">
                          {t('inTheatersOnDate', 'In theaters on {{date}}', { date: dateDisplayLong })}
                        </CaptionMD>
                      </Case>
                      <Case condition={isGuildEarlyAccess}>
                        <PlaySquareIcon color="info-200" size={24} />
                        <CaptionMD color="info-200">
                          {t('streamingForGuildMembers', 'Streaming for Guild Members on {{date}}', {
                            date: dateDisplayLong,
                          })}
                        </CaptionMD>
                      </Case>
                      <Case condition={isPublicAccess}>
                        <PlaySquareIcon color="info-200" size={24} />
                        <CaptionMD color="info-200">
                          {t('streamingForEveryone', 'Streaming free for everyone on {{date}}', {
                            date: dateDisplayLong,
                          })}
                        </CaptionMD>
                      </Case>
                    </Switch>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <Switch>
                      <Case condition={isGuildMember && (isGuildEarlyAccess || isTheatrical)}>
                        <CircleCheckIcon color1="gray-925" color2="success-200" size={24} />
                        <CaptionMD color="success-200">
                          {t('includedWithGuildMembership', 'Included with your Guild membership')}
                        </CaptionMD>
                      </Case>
                      <Case condition={!isGuildMember && (isGuildEarlyAccess || isTheatrical)}>
                        <GuildIconMono color="core-warning-200" size={24} />
                        <CaptionMD color="core-warning-200">
                          {t('includedForGuildMembers', 'Included for members of the Angel Guild')}
                        </CaptionMD>
                      </Case>
                    </Switch>
                  </div>
                </div>
                <div className="mt-8 flex flex-row gap-8">
                  <When condition={!isTheatrical}>
                    <LinkButton href={linkModel.linkUrl} variant="white" outline>
                      <CircleInfoIcon className="mr-2" color="white" size={24} />
                      {t('viewDetails', 'View Details')}
                    </LinkButton>
                  </When>
                  <When condition={isTheatrical}>
                    <LinkButton href={linkModel.linkUrl} variant="white" outline>
                      <TicketIcon className="mr-2" color="white" size={24} />
                      {isGuildMember ? t('claimTickets', 'Claim Tickets') : t('getShowtimes', 'Get Showtimes')}
                    </LinkButton>
                  </When>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    </LazyHydrate>
  )
}
